<template>
  <div>
    <div class="mandala-evaluation-modal__final-goal">
      <img
        src="@/assets/images/logo@2x.png"
        class="mandala-evaluation-modal__logo">
      <span class="pl-2">
        <ChangedBoxContainer
          :active="hasFinalGoalChanged">
          {{ latestMandalaEvaluation.finalGoal }}
        </ChangedBoxContainer>
        <span class="pl-3">
          まで残り
          <template v-if="targetDateMonthDiff < 1">
            {{ targetDateDiff > 0 ? targetDateDiff : 0 }}
            日
          </template>
          <template v-else>
            {{ targetDateMonthDiff }}ヶ月
          </template>
        </span>
      </span>
    </div>
    <div
      v-if="canShowBody"
      class="mandala-evaluation-modal__entries-wrapper">
      <div
        v-for="(entry, entryIndex) in latestMandalaEvaluationFilteredEntryItems"
        :key="entry.id"
        class="mandala-evaluation-modal__entry-list-item">
        <div
          v-if="canShowEntryName && entry.name"
          class="mandala-evaluation-modal__entry-list-item-name">
          <ChangedBoxContainer
            :text="entry.name"
            :active="hasEntryNameChanged(entryIndex)">
            <template #middle>
              {{ `${entry.displayOrder + 1} - ` }}
            </template>
            {{ entry.name }}
          </ChangedBoxContainer>
        </div>
        <template v-if="canShowActionGoalNames">
          <ul
            v-for="(actionGoal, actionGoalIndex) in entry.mandalaGoalSelfEvaluationActionGoals"
            :key="actionGoal.id"
            class="mandala-evaluation-modal__action-goal-list">
            <li
              class="mandala-evaluation-modal__action-goal-list-item">
              <ChangedBoxContainer
                :active="hasActionGoalChanged(entryIndex, actionGoalIndex)">
                <template #middle>
                  <img
                    src="@/assets/images/icon_flag-b.svg"
                    class="flag-icon mr-2"
                    style="width:12px;height:12px;">
                </template>
                {{ actionGoal.name }}
              </ChangedBoxContainer>
            </li>
          </ul>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { differenceInDays, differenceInMonths } from 'date-fns'
import ChangedBoxContainer from '@/components/parts/ChangedBoxContainer.vue'
export default {
  components: {
    ChangedBoxContainer
  },
  props: {
    mandalaEvaluations: {
      type: Array,
      required: true
    },
    mandalaGoalTemplate: {
      type: Object,
      required: true
    },
    isMine: {
      type: Boolean,
      default: false
    },
    isManager: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    latestMandalaEvaluation() {
      return this.mandalaEvaluations[0]
    },
    lastMonthMandalaEvaluation() {
      return this.mandalaEvaluations[1] || null
    },
    canShowEntryName() {
      return this.isMine || this.isManager || !this.mandalaGoalTemplate.isLockEntryNames
    },
    canShowActionGoalNames() {
      return this.isMine || this.isManager || !this.mandalaGoalTemplate.isLockActionGoalNames
    },
    canShowBody() {
      return this.canShowEntryName || this.canShowActionGoalNames
    },
    targetDateMonthDiff() {
      return differenceInMonths(new Date(this.latestMandalaEvaluation.targetDate), new Date())
    },
    targetDateDiff() {
      return differenceInDays(new Date(this.latestMandalaEvaluation.targetDate), new Date())
    },
    /**
     * 表示に必要なデータのみフィルタリング
     */
    latestMandalaEvaluationFilteredEntryItems() {
      if (!this.latestMandalaEvaluation) {
        return null
      }
      return this.filteredEntryItems(this.latestMandalaEvaluation)
    },
    hasFinalGoalChanged() {
      if (!this.lastMonthMandalaEvaluation) {
        return false
      }
      return (this.lastMonthMandalaEvaluation.finalGoal || '') !== (this.latestMandalaEvaluation.finalGoal || '')
    },
  },
  methods: {
    filteredEntryItems(mandalaEvaluation) {
      return mandalaEvaluation.mandalaGoalSelfEvaluationEntries
        .filter(entry => !!entry.name || entry.mandalaGoalSelfEvaluationActionGoals.some(actionGoal => !!actionGoal.name))
        .map(entry => ({
          ...entry,
          mandalaGoalSelfEvaluationActionGoals: entry.mandalaGoalSelfEvaluationActionGoals.filter(actionGoal => !!actionGoal.name)
        })
        )
    },
    getEntryNameFromMandalaEvaluation(mandalaEvaluation, entryIndex) {
      if (!mandalaEvaluation) {
        return null
      }
      return this.filteredEntryItems(mandalaEvaluation)[entryIndex]?.name || null
    },
    getActionNameFromMandalaEvaluation(mandalaEvaluation, entryIndex, actionGoalIndex) {
      if (!mandalaEvaluation) {
        return null
      }
      return this.filteredEntryItems(mandalaEvaluation)[entryIndex]?.mandalaGoalSelfEvaluationActionGoals[actionGoalIndex]?.name || null
    },
    hasEntryNameChanged(entryIndex) {
      if (!this.lastMonthMandalaEvaluation) {
        return false
      }
      return this.getEntryNameFromMandalaEvaluation(this.latestMandalaEvaluation, entryIndex) !== this.getEntryNameFromMandalaEvaluation(this.lastMonthMandalaEvaluation, entryIndex)
    },
    hasActionGoalChanged(entryIndex, actionGoalIndex) {
      if (!this.lastMonthMandalaEvaluation) {
        return false
      }
      return this.getActionNameFromMandalaEvaluation(this.latestMandalaEvaluation, entryIndex, actionGoalIndex) !== this.getActionNameFromMandalaEvaluation(this.lastMonthMandalaEvaluation, entryIndex, actionGoalIndex)
    }
  }
}
</script>

<style lang="scss" scoped>
.mandala-evaluation-modal__logo {
  width: 50px;
}
.mandala-evaluation-modal {

  &__final-goal {
    display: flex;
    align-items: center;
    background-color: $white;
    border-radius: 0.3em;
    padding: 16px;
    margin-bottom: 12px;
    font-size: $fs-nm;
    font-weight: bold;
  }

  &__entries-wrapper {
    background-color: $white;
    border-radius: 0.3em;
    padding: 16px;
  }
  
  &__content.modal-content{
    position: relative;
    background-color: transparent;
    border: none;
    margin-top: calc(env(safe-area-inset-top));
    margin-bottom: 60px;
  }
  
  &__header.modal-header {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    background-color: transparent;
    margin-bottom: 12px;
    border: none;
    padding: 0;
  }
  &__body.modal-body {
    background-color: transparent;
    padding: 0;
    font-size: 12px;
    font-weight: bold;
    &.is-locked {
      background-color: transparent;
    }
  }
  &__entry-list {
    &-item {
      font-size: $fs-lg;
      padding: 16px 0;
      border-bottom: 1px solid $grey02;
      
      &-name {
        padding-bottom: 8px;
      }
      &:first-child {
        padding: 0 0 16px 0;
      }
      &:last-child {
        border-bottom: none;
      }
    }
  }
  &__action-goal-list {
    list-style-type: none;
    padding-left: 0;
    &-item {
      font-size: $fs-m;
      padding-bottom: 8px;
    }
  }
}
</style>